import React, { useState, useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import {
  close, searchOutline, navigateOutline, addOutline,
  chevronUpOutline, chevronDownOutline,
} from 'ionicons/icons';
import type { MapFamily, VisitStatus } from '@/api/maps';
import { VISIT_TYPE_LABELS, VISIT_TYPE_COLORS } from '@/utils/labels';
import type { VisitType } from '@/types/visit';

const STATUS_ORDER: Record<VisitStatus, number> = { gray: 0, red: 1, yellow: 2, green: 3 };

interface SectorFamilyPanelProps {
  families: MapFamily[];
  sectorName: string;
  pastoralOnly: boolean;
  onClose: () => void;
  onFlyTo: (family: MapFamily) => void;
  onAddVisit: (familyId: string) => void;
  onHover?: (familyId: string | null) => void;
}

/** Strip diacritics for Romanian-friendly matching */
function normalize(str: string): string {
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

type SortKey = 'status' | 'name';

const SectorFamilyPanel: React.FC<SectorFamilyPanelProps> = ({
  families, sectorName, pastoralOnly, onClose, onFlyTo, onAddVisit, onHover,
}) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortAsc, setSortAsc] = useState(true);

  const getStatus = (f: MapFamily): VisitStatus =>
    pastoralOnly ? (f.pastoral_visit_status || 'gray') : (f.visit_status || 'gray');

  const sorted = useMemo(() => {
    let list = [...families];

    if (search.trim()) {
      const q = normalize(search);
      list = list.filter((f) => {
        const name = normalize(f.display_name ?? f.name);
        const addr = normalize(f.address ?? '');
        return name.includes(q) || addr.includes(q);
      });
    }

    list.sort((a, b) => {
      if (sortKey === 'status') {
        const diff = STATUS_ORDER[getStatus(a)] - STATUS_ORDER[getStatus(b)];
        if (diff !== 0) return sortAsc ? diff : -diff;
        return (a.display_name ?? a.name).localeCompare(b.display_name ?? b.name, 'ro');
      }
      const cmp = (a.display_name ?? a.name).localeCompare(b.display_name ?? b.name, 'ro');
      return sortAsc ? cmp : -cmp;
    });

    return list;
  }, [families, search, sortKey, sortAsc, pastoralOnly]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const statusColors: Record<VisitStatus, string> = {
    green: '#059669', yellow: '#d97706', red: '#dc2626', gray: '#94a3b8',
  };

  const statusLabels: Record<VisitStatus, string> = {
    green: '< 6 luni', yellow: '6-12 luni', red: '> 12 luni', gray: 'Niciodată',
  };

  const formatDate = (d: string | null) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const SortIcon: React.FC<{ active: boolean; asc: boolean }> = ({ active, asc }) =>
    active ? (
      <IonIcon icon={asc ? chevronUpOutline : chevronDownOutline} style={{ fontSize: 12 }} />
    ) : null;

  return (
    <div className="sfpanel">
      {/* Header */}
      <div className="sfpanel__header">
        <div className="sfpanel__header-top">
          <h3 className="sfpanel__title">{sectorName}</h3>
          <span className="sfpanel__count">{families.length} familii</span>
          <button className="sfpanel__close" onClick={onClose}>
            <IonIcon icon={close} />
          </button>
        </div>
        {pastoralOnly ? (
          <div className="sfpanel__filter-badge">Doar vizite pastorale</div>
        ) : null}
      </div>

      {/* Search */}
      <div className="sfpanel__search">
        <IonIcon icon={searchOutline} className="sfpanel__search-icon" />
        <input
          type="text"
          className="sfpanel__search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Caută familie..."
        />
        {search ? (
          <button className="sfpanel__search-clear" onClick={() => setSearch('')}>
            <IonIcon icon={close} />
          </button>
        ) : null}
      </div>

      {/* Sort controls */}
      <div className="sfpanel__sort">
        <button
          className={`sfpanel__sort-btn${sortKey === 'status' ? ' sfpanel__sort-btn--active' : ''}`}
          onClick={() => handleSort('status')}
        >
          Status <SortIcon active={sortKey === 'status'} asc={sortAsc} />
        </button>
        <button
          className={`sfpanel__sort-btn${sortKey === 'name' ? ' sfpanel__sort-btn--active' : ''}`}
          onClick={() => handleSort('name')}
        >
          Nume <SortIcon active={sortKey === 'name'} asc={sortAsc} />
        </button>
        <span className="sfpanel__sort-count">{sorted.length} rezultate</span>
      </div>

      {/* Family list */}
      <div className="sfpanel__list">
        {sorted.map((family) => {
          const status = getStatus(family);
          const color = statusColors[status];
          const pastoralDate = formatDate(family.last_pastoral_visit_date);
          const anyDate = formatDate(family.last_visit_date);
          const displayDate = pastoralOnly ? pastoralDate : anyDate;
          const hasLocation = !!family.latitude && !!family.longitude;

          return (
            <div
              key={family.id}
              className="sfpanel__row"
              onMouseEnter={() => onHover?.(family.id)}
              onMouseLeave={() => onHover?.(null)}
              onClick={() => { if (hasLocation) onFlyTo(family); }}
              style={{ cursor: hasLocation ? 'pointer' : 'default' }}
            >
              <span className="sfpanel__row-dot" style={{ background: color, boxShadow: `0 0 0 3px ${color}20` }} />
              <div className="sfpanel__row-info">
                <span className="sfpanel__row-name">{family.display_name ?? family.name}</span>
                {family.address ? (
                  <span className="sfpanel__row-addr">{[family.address, family.numar ? `Nr. ${family.numar}` : ''].filter(Boolean).join(' ')}</span>
                ) : null}
                <div className="sfpanel__row-meta">
                  <span className="sfpanel__row-status" style={{ color }}>
                    {statusLabels[status]}
                    {displayDate ? ` · ${displayDate}` : ''}
                  </span>
                  {family.last_visit_type ? (
                    <span
                      className="sfpanel__row-type"
                      style={{ color: VISIT_TYPE_COLORS[family.last_visit_type as VisitType] ?? '#64748b' }}
                    >
                      {VISIT_TYPE_LABELS[family.last_visit_type as VisitType] ?? family.last_visit_type}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="sfpanel__row-actions">
                <button
                  className="sfpanel__row-btn sfpanel__row-btn--visit"
                  title="Adaugă vizită"
                  onClick={(e) => { e.stopPropagation(); onAddVisit(family.id); }}
                >
                  <IonIcon icon={addOutline} />
                </button>
                {hasLocation ? (
                  <button
                    className="sfpanel__row-btn"
                    title="Arată pe hartă"
                    onClick={(e) => { e.stopPropagation(); onFlyTo(family); }}
                  >
                    <IonIcon icon={navigateOutline} />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
        {sorted.length === 0 ? (
          <div className="sfpanel__empty">
            {search ? `Nicio familie găsită pentru „${search}"` : 'Nicio familie în acest sector'}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SectorFamilyPanel;
